import React, { useMemo } from "react";

import Tooltip from "@busbud/design-system-components/dist/Tooltip/Tooltip";
import { IconInfoCircle } from "@busbud/horizon";

import type { LineItemProps } from "@app/components/trip-fare-summary/line-item";
import { LineItem } from "@app/components/trip-fare-summary/line-item";
import { MERGE_FEES_SOURCES } from "@app/constants/sources";
import { useAppContext } from "@app/helpers/hooks";

interface SupplierFeeLineProps extends Omit<LineItemProps, "label"> {
  operator_name: string;
}

const SupplierFeeItem: React.FC<SupplierFeeLineProps> = props => {
  const { translator } = useAppContext();
  const { amount, operator_name, ...other_props } = props;

  const supplier_display_name =
    operator_name ?? translator.t("!checkout.summary.fees-taxes.fees.operator");
  const supplier_fee_label = translator.t(
    "!checkout.summary.fees-taxes.fees.operator.fees",
    { operator: supplier_display_name }
  );

  return (
    <LineItem
      amount={amount}
      data_testid="supplier_fee"
      label={supplier_fee_label}
      {...other_props}
    />
  );
};

export interface FeesLinesProps extends Omit<LineItemProps, "label"> {
  busbud_fee: number | undefined;
  is_loading: boolean;
  operator_name: string;
  source_names: string[];
}

export const FeesLines: React.FC<FeesLinesProps> = props => {
  const { translator } = useAppContext();
  const { amount, busbud_fee, operator_name, source_names, ...other_props } =
    props;

  const merge_fees = useMemo(
    () =>
      source_names.some(source_name =>
        MERGE_FEES_SOURCES.includes(source_name)
      ),
    [source_names]
  );

  let supplier_fee = busbud_fee ? amount - busbud_fee : amount;
  let platform_fee = busbud_fee ?? 0;

  if (merge_fees) {
    supplier_fee = 0;
    platform_fee = amount;
  }

  return (
    <>
      {supplier_fee > 0 && (
        <SupplierFeeItem
          amount={supplier_fee}
          operator_name={operator_name}
          data_testid="supplier_fee"
          {...other_props}
        />
      )}
      {platform_fee > 0 && (
        <LineItem
          amount={platform_fee}
          data_testid="platform_fee"
          label={translator.t("!checkout.summary.fees-taxes.fees.platform")}
          tooltip={
            <Tooltip
              data-testid="platform_fee_tooltip"
              arrow
              placement="top"
              title={translator.t("!checkout.priceinfo.fees.platform.tooltips")}
            >
              <span>
                <IconInfoCircle size="sm" className="text-color-tertiary" />
              </span>
            </Tooltip>
          }
          {...other_props}
        />
      )}
    </>
  );
};
